import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast} from '@hm/fundamental'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {createEvent} from '../helpers/createEvent'
import {EventCreate, EventUpdate, ShiftEvent} from '../types'
import {isTask} from '../utils/eventType'
import {prepareEventFormData} from '../utils/formData'

type UseEventCreateParams = {
  plantId: string
  timezone: string
  options?: UseMutationOptions<ShiftEvent, AxiosError, EventCreate>
}

export const useEventCreate = ({plantId, timezone, options}: UseEventCreateParams) => {
  const queryClient = useQueryClient()
  const {t} = useTranslation()

  return useMutation({
    mutationFn: (event: EventCreate) => {
      const scheduleRecurringTask = isTask(event) && !!event.repetitionInfo
      return createEvent(ApiClient.axiosInstance, plantId, event, timezone, scheduleRecurringTask)
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      if (isTask(data) && data.isChecklistsCreationFailed) {
        spawnErrorToast(t('shiftEvent.checklist.action.createFailed'))
      }
      await queryClient.invalidateQueries()
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}

/**
 * Special case for converting a simple task to recurring task
 * For editing a simple task to add recurring info, we 'POST' the data to tasks/schedules api
 * to handle the scheduling of repetitive Task independent from original create Event logic
 * the backend team decided to use a dedicated endpoint with the same DTO
 */
type UseCreateNonRecToRecEventParams = {
  plantId: string
  options?: UseMutationOptions<ShiftEvent, AxiosError, EventUpdate>
}

export const useCreateNonRecToRecEvent = ({plantId, options}: UseCreateNonRecToRecEventParams) => {
  const queryClient = useQueryClient()
  const {t} = useTranslation()

  return useMutation({
    mutationFn: async (event: EventUpdate) => {
      const formData = await prepareEventFormData(event, ['attachments'])

      const path = `/shifthandover/${plantId}/tasks/schedules`
      const response = await ApiClient.axiosInstance.post<ShiftEvent>(path, formData)
      return response.data
    },
    ...options,
    onSuccess: async (data) => {
      if (isTask(data) && data.isChecklistsCreationFailed) {
        spawnErrorToast(t('shiftEvent.checklist.action.createFailed'))
      }
      await queryClient.invalidateQueries()
    }
  })
}
